import React from 'react';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import settings from '../../constants/settings';

const BlogPost = ({ post }) => {
  // Format date to "dd mmmm yyyy" format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('id-ID', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };
  
  if (!post) return <div>Loading...</div>;
  
  return (
    <article className="max-w-4xl mx-auto px-4">
      {/* Back to Posts Link */}
      <Link to="/blog" className="inline-flex items-center text-gold hover:text-cream transition-colors mb-8">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Kembali ke Semua Artikel
      </Link>
      
      {/* Header */}
      <header className="mb-12">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6 leading-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center text-base text-gray-600 mb-6">
          <div className="flex items-center mr-6 mb-2">
            <User className="h-5 w-5 mr-2" />
            <span className="text-base font-medium">{post.author}</span>
          </div>
          <div className="flex items-center mb-2">
            <Calendar className="h-5 w-5 mr-2" />
            <span className="text-base">{formatDate(post.date)}</span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-8">
          {post.tags && Array.isArray(post.tags) ? post.tags.map((tag, index) => (
            <span key={index} className="inline-flex items-center text-xs font-medium bg-cream/30 text-dark-navy px-2.5 py-0.5 rounded-full">
              <Tag className="w-3 h-3 mr-1" />
              {tag}
            </span>
          )) : (
            <span className="inline-flex items-center text-xs font-medium bg-cream/30 text-dark-navy px-2.5 py-0.5 rounded-full">
              <Tag className="w-3 h-3 mr-1" />
              {post.tags}
            </span>
          )}
        </div>
        
        {/* Cover Image */}
        {post.coverImage && (
          <div className="aspect-[16/9] mb-10 overflow-hidden rounded-lg">
            <img 
              src={post.coverImage} 
              alt={post.title} 
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </header>
      
      {/* Blog Content */}
      <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-gold prose-a:no-underline hover:prose-a:text-cream prose-img:rounded-md mb-12 markdown-content custom-markdown">
        <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
          {post.content}
        </ReactMarkdown>
      </div>
      
      {/* Author Bio */}
      <div className="border-t border-gray-200 pt-8 mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Tentang Penulis</h3>
        <p className="text-gray-600">
          {post.author} adalah kontributor di #PintarPilihKuliah yang berbagi pengalaman dan informasi seputar persiapan studi di luar negeri.
        </p>
      </div>
      
      {/* CTA */}
      <div className="bg-cream/30 border border-cream rounded-lg p-6 mt-10">
        <h3 className="text-xl font-serif font-bold text-gray-900 mb-3">
          Butuh Bantuan Lebih Lanjut?
        </h3>
        <p className="text-gray-600 mb-4">
          Tim #PintarPilihKuliah siap membantu persiapan studi luar negeri kamu. Konsultasikan rencana pendidikanmu dengan para ahli kami.
        </p>
        <a
          href={settings.wa_consultant_link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center bg-gold hover:bg-dark-navy text-white font-medium rounded-md px-5 py-2.5 transition-colors"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
          </svg>
          Konsultasi Sekarang
        </a>
      </div>
    </article>
  );
};

export default BlogPost;